/** Surface matrix for the app-wide task context menu (right-click). */
// @ts-check

/** @typedef {'today'|'today-overdue'|'today-upcoming'|'organize-master'|'organize-detail'|'organize-inbox'|'plan-rail'|'plan-grid'|'plan-week-month'|'dashboard'} TaskContextMenuSurface */

/** @typedef {'assign'|'delete'|'addSubtask'|'moveToTomorrow'|'moveToToday'|'moveToBacklog'|'moveToActive'} TaskContextMenuAction */

/**
 * @typedef {object} TaskContextMenuSurfaceConfig
 * @property {boolean} assign
 * @property {boolean} delete
 * @property {boolean} addSubtask
 * @property {boolean} moveToTomorrow
 * @property {boolean} moveToToday
 * @property {boolean} moveToBacklog
 * @property {boolean} moveToActive
 * @property {boolean} deleteOnRow
 */

/** @type {Record<TaskContextMenuSurface, TaskContextMenuSurfaceConfig>} */
export const TASK_CONTEXT_MENU_SURFACES = {
  today: {
    assign: true,
    delete: true,
    addSubtask: false,
    moveToTomorrow: true,
    moveToToday: false,
    moveToBacklog: false,
    moveToActive: false,
    deleteOnRow: false,
  },
  'today-overdue': {
    assign: true,
    delete: true,
    addSubtask: false,
    moveToTomorrow: false,
    moveToToday: true,
    moveToBacklog: false,
    moveToActive: false,
    deleteOnRow: false,
  },
  'today-upcoming': {
    assign: true,
    delete: true,
    addSubtask: false,
    moveToTomorrow: true,
    moveToToday: false,
    moveToBacklog: false,
    moveToActive: false,
    deleteOnRow: false,
  },
  'organize-master': {
    assign: true,
    delete: true,
    addSubtask: false,
    moveToTomorrow: false,
    moveToToday: false,
    moveToBacklog: true,
    moveToActive: true,
    deleteOnRow: false,
  },
  'organize-detail': {
    assign: true,
    delete: true,
    addSubtask: false,
    moveToTomorrow: false,
    moveToToday: false,
    moveToBacklog: false,
    moveToActive: false,
    deleteOnRow: false,
  },
  'plan-rail': {
    assign: true,
    delete: true,
    addSubtask: true,
    moveToTomorrow: false,
    moveToToday: false,
    moveToBacklog: false,
    moveToActive: false,
    deleteOnRow: false,
  },
  'plan-grid': {
    assign: true,
    delete: true,
    addSubtask: false,
    moveToTomorrow: false,
    moveToToday: false,
    moveToBacklog: false,
    moveToActive: false,
    deleteOnRow: false,
  },
  'plan-week-month': {
    assign: false,
    delete: true,
    addSubtask: false,
    moveToTomorrow: false,
    moveToToday: false,
    moveToBacklog: false,
    moveToActive: false,
    deleteOnRow: false,
  },
  dashboard: {
    assign: false,
    delete: true,
    addSubtask: false,
    moveToTomorrow: false,
    moveToToday: false,
    moveToBacklog: false,
    moveToActive: false,
    deleteOnRow: false,
  },
  'organize-inbox': {
    assign: true,
    delete: true,
    addSubtask: false,
    moveToTomorrow: false,
    moveToToday: false,
    moveToBacklog: false,
    moveToActive: false,
    deleteOnRow: false,
  },
};

/**
 * @param {TaskContextMenuSurface|null|undefined} surface
 * @returns {TaskContextMenuSurfaceConfig|null}
 */
export const getSurfaceMenuConfig = (surface) => {
  if (!surface) return null;
  return TASK_CONTEXT_MENU_SURFACES[surface] ?? null;
};

/**
 * @param {TaskContextMenuSurface|null|undefined} surface
 * @returns {TaskContextMenuAction[]}
 */
export const getEnabledMenuActions = (surface) => {
  const config = getSurfaceMenuConfig(surface);
  if (!config) return [];
  /** @type {TaskContextMenuAction[]} */
  const actions = [];
  if (config.assign) actions.push('assign');
  if (config.delete) actions.push('delete');
  if (config.addSubtask) actions.push('addSubtask');
  if (config.moveToTomorrow) actions.push('moveToTomorrow');
  if (config.moveToToday) actions.push('moveToToday');
  if (config.moveToBacklog) actions.push('moveToBacklog');
  if (config.moveToActive) actions.push('moveToActive');
  return actions;
};

/**
 * @param {TaskContextMenuSurface|null|undefined} surface
 * @returns {boolean}
 */
export const shouldShowRowDelete = (surface) =>
  Boolean(getSurfaceMenuConfig(surface)?.deleteOnRow);
